import React from 'react'
import papa from 'papaparse'
import { filter, omitBy, some } from 'lodash'
import {
  AddCompanyGroupMembers_addCompanyGroupMembers,
  useAddCompanyGroupMembersMutation,
  GroupMemberRole,
  AddCompanyGroupMembersUserInput,
  UserRole,
  useGroupForm,
} from '@modmd/data'
import { DEVICE } from 'theme'
import { formatRaceToGql, formatEthnicityToGql, formatGenderToGql } from 'utils/helpers'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import Button from 'components/Button'
import TextInput from 'components/TextInput'
import Card from 'components/Card'
import { Dialog } from 'components/Dialog'
import Datalist from 'components/Datalist'
import Icon from 'components/Icon/Icon'
import { SelectInput } from 'components/SelectInput'

enum CsvFormat {
  ModMDMigration = 'ModMDMigration',
  Email = 'Email',
}

interface Props {
  companyId: string
  groupId: string
  onUploadSuccess: (users: AddCompanyGroupMembers_addCompanyGroupMembers[]) => void
  csvFormat?: keyof typeof CsvFormat
}

const formatRow = (format: keyof typeof CsvFormat) => (data: string[]) => {
  if (format === 'Email') {
    const userEmail = data[0].trim()
    return {
      User: {
        email: userEmail,
      },
      role: GroupMemberRole.Member,
    }
  }
  if (format === 'ModMDMigration') {
    const [
      firstName,
      ,
      lastName,
      gender,
      birthDate,
      ,
      email,
      phoneNumber,
      street,
      city,
      county,
      ,
      zip,
      race,
      ,
      ethnicity,
      state,
      country,
    ] = data
    const userEmail = email.trim()
    return {
      User: {
        firstName,
        lastName,
        gender: formatGenderToGql(gender),
        birthDate,
        email: userEmail,
        phoneNumber,
        race: formatRaceToGql(race),
        ethnicity: formatEthnicityToGql(ethnicity),
        Address: {
          street,
          city,
          zip,
          country,
          county,
          state,
        },
      },
      role: GroupMemberRole.Member,
    }
  }
  return undefined
}

const CsvInviteNewGroupMembers: React.VFC<Props> = ({
  groupId,
  companyId,
  onUploadSuccess,
  csvFormat: providedCsvFormat = 'Email',
}) => {
  const [csvFormat, setCsvFormat] = React.useState(providedCsvFormat)
  const [csvData, setCsvData] = React.useState<string[][]>()
  React.useEffect(() => {
    setCsvFormat(providedCsvFormat)
  }, [providedCsvFormat])
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const [isOpenNewUserDialog, setIsOpenNewUserDialog] = React.useState(false)
  const [
    addCompanyGroupMembers,
    addCompanyGroupMembersMutation,
  ] = useAddCompanyGroupMembersMutation()
  const { groupForm } = useGroupForm({
    validationSchema: 'membersOnly',
    onSubmit: async (values, formikHelpers) => {
      try {
        const { data } = await addCompanyGroupMembers({
          variables: {
            inputData: {
              companyId,
              groupId,
              Members: values.Members.filter(({ User }) => !!User.email).map(({ User, role }) => ({
                role,
                User: {
                  ...omitBy(User, (value) => !value),
                  roles: [UserRole.PATIENT],
                  email: User.email,
                },
              })) as AddCompanyGroupMembersUserInput[],
            },
          },
        })
        if (data) {
          formikHelpers.resetForm()
          setIsOpenNewUserDialog(false)
          onUploadSuccess(data.addCompanyGroupMembers)
        }
      } catch {
        // ignore
      }
    },
  })

  React.useEffect(() => {
    if (csvData?.length) {
      void groupForm.setFieldValue('Members', csvData.map(formatRow(csvFormat)))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csvFormat, csvData])

  const handleOpenNewUserDialog = React.useCallback(() => {
    setIsOpenNewUserDialog(true)
  }, [])

  const handleCloseNewUserDialog = React.useCallback(() => {
    void groupForm.setFieldValue('Members', [])
    setIsOpenNewUserDialog(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleCsvUpload = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files?.length) {
        papa.parse(event.target.files[0], {
          complete: ({ data }: { data: string[][] }) => {
            const [, ...rows] = data
            setCsvData(filter(rows, some))
            void groupForm.setFieldValue('Members', rows.map(formatRow(csvFormat)))
          },
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(groupForm.values.Members), csvFormat]
  )

  const handleDeleteMember = React.useCallback(
    (index: number) => {
      void groupForm.setFieldValue(
        'Members',
        groupForm.values.Members.filter((_, memberIndex) => memberIndex !== index)
      )
    },
    [groupForm]
  )

  const handleSelectMemberRole = React.useCallback(
    ({ role, email }: { role: GroupMemberRole; email: string }) => {
      if (email) {
        void groupForm.setFieldValue(
          'Members',
          groupForm.values.Members.map((companyMember) =>
            companyMember.User.email === email ? { ...companyMember, role } : companyMember
          )
        )
      }
    },
    [groupForm]
  )

  return (
    <>
      <Button marginLeft="auto" onClick={handleOpenNewUserDialog} leftIcon={<Icon.Plus size="L" />}>
        Import csv
      </Button>
      <Dialog
        variant={isDesktop ? 'right' : 'fullscreen'}
        maxWidth={isDesktop ? '550px' : '100%'}
        isOpen={isOpenNewUserDialog}
        onDismiss={handleCloseNewUserDialog}
      >
        <Card.Title onDismiss={handleCloseNewUserDialog}>Import members</Card.Title>
        <Card.Content>
          <SelectInput
            label="Import format"
            value={csvFormat}
            options={Object.values(CsvFormat).map((value) => ({ label: value, value }))}
            onChange={(e) => setCsvFormat(e.target.value as CsvFormat)}
          />
          <TextInput
            key={groupForm.values.Members.length}
            type="file"
            onChange={handleCsvUpload}
            label="Select csv file"
          />
          {groupForm.values.Members.length > 0 && (
            <Datalist
              header={<Datalist.Title>Members</Datalist.Title>}
              footer={
                <Button
                  onClick={() => {
                    groupForm.handleSubmit()
                  }}
                  disabled={addCompanyGroupMembersMutation.loading}
                >
                  Save members
                </Button>
              }
              data={groupForm.values.Members}
              renderTableHeader={() => (
                <Datalist.Row display="grid" gridTemplateColumns="repeat(2, 1fr) 2.5rem">
                  <Datalist.HeaderCell label="Email" />
                  <Datalist.HeaderCell label="Role" />
                  <Datalist.HeaderCell label="Actions" />
                </Datalist.Row>
              )}
              shouldRenderTableHeader={isDesktop}
              renderTableRow={(member, index) => (
                <Datalist.Row display="grid" gridTemplateColumns="repeat(2, 1fr) 2.5rem">
                  <Datalist.Cell label="Email">{member.User.email}</Datalist.Cell>
                  <Datalist.Cell label="Role">
                    <SelectInput
                      value={member.role}
                      name="memberRole"
                      size="small"
                      onChange={(event) =>
                        handleSelectMemberRole({
                          role: event.target.value as GroupMemberRole,
                          email: member.User.email,
                        })
                      }
                      options={[
                        {
                          label: 'Member',
                          value: GroupMemberRole.Member,
                        },
                        {
                          label: 'Manager',
                          value: GroupMemberRole.Manager,
                        },
                        {
                          label: 'Coordinator',
                          value: GroupMemberRole.Coordinator,
                        },
                      ]}
                      hasBottomContent={false}
                      hasTopContent={false}
                    />
                  </Datalist.Cell>
                  <Datalist.Cell label="Actions">
                    <Button
                      onClick={() => handleDeleteMember(index)}
                      appearance="ghost"
                      colorVariant="danger"
                      size="small"
                    >
                      <Icon.Trash />
                    </Button>
                  </Datalist.Cell>
                </Datalist.Row>
              )}
            />
          )}
        </Card.Content>
      </Dialog>
    </>
  )
}

export { CsvInviteNewGroupMembers }
